import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DESTINATION_STATE_CHANGE, getDestinationState, getStateInfo } from '@theme/destination-state';

const STATE_PLACEHOLDER = '{state}';

/**
 * Mobile sticky reserve bar (FindARancherDemo ProductPage.tsx BuyBox):
 * shows a fixed bottom bar duplicating the Reserve CTA once the primary
 * reserve area has scrolled above the viewport. The demo drives this with
 * a scroll listener reading `getBoundingClientRect().bottom < 0`; this port
 * uses IntersectionObserver instead (the project's own convention —
 * `11-performance.md` "Observers over listeners") watching the same
 * condition: the bar shows once the reserve area is both NOT intersecting
 * the viewport and has scrolled above it (an entry that is not intersecting
 * because it is still below the viewport keeps the bar hidden).
 *
 * It also owns the in-flight state of both Reserve buttons: the stock
 * product form dispatches the standard `shopify:cart:lines-update` event
 * (action "add", with a promise) BEFORE its add-to-cart request. While that
 * promise is pending this component carries `_loading` + `aria-busy`, and
 * both stock <add-to-cart-component>s inside it are disabled through their
 * public `disable()` / `enable()` methods — never by reaching into their
 * markup. The stock form does not disable its button itself.
 *
 * Cart limit — Shopify inventory only (no per-ranch / per-share rule): the
 * server renders the initial state from snippets/ranch-cart-cap.liquid —
 * `_limit` + `aria-disabled` on this element, both buttons disabled, the
 * limit sentence in every `limitMessages[]` ref. The cart can change without
 * a PDP reload (drawer quantity / remove, or this form's own add), so after
 * EVERY standard `shopify:cart:lines-update` settles this component re-runs
 * the same check client-side: it reads the cart lines from the event's
 * resolved `detail.items` (falling back to `/cart.js`), evaluates them
 * against the snippet's `client` JSON (ref `cartCapConfig`: variant,
 * inventory, sold-out flag and the sentences), then toggles `_limit` /
 * `aria-disabled`, rewrites the limit sentences and enables / disables the
 * buttons through their public API. Keep `#evaluateCart` in step with the
 * snippet — it is the client mirror of that one implementation. Also
 * refreshes on a bfcache restore.
 *
 * No service — the ranch does not ship to the shopper's destination state
 * (the cart attribute, assets/destination-state.js): the server renders the
 * same `_limit` state plus `_no-service`, which swaps each CTA's label for
 * its `.js-pdp-no-service-label` span ("Doesn't ship to {state}", the
 * `data-no-service-message` template, snippets/pdp-buy-box.liquid) when the
 * state is not in `data-lanes` (the ranch's ships_to) — no sentence under
 * the buttons. It outranks the inventory reasons. On every
 * `destination-state:change` the component re-evaluates with the last cart
 * lines it saw (or fetches them), rewrites the state name in those spans and
 * toggles `_no-service`, so the header's state picker enables / disables the
 * buttons without a reload. No state at all (blank) never blocks, like the
 * server.
 *
 * Payment choice — a reserve product with a Downpay plan (snippets/
 * pdp-buy-box.liquid "Payment choice"): the "Pay a deposit" tile (the
 * default) keeps the form's hidden `selling_plan` input enabled so the add
 * carries the plan and checkout charges the deposit; "Pay in full" disables
 * it. The buy box root also carries `_full-payment` so its server-rendered
 * full-payment copies show in place of the deposit ones — no text is
 * written here. Re-applied on a bfcache restore, when the browser brings
 * the tiles back in their last state.
 *
 * @typedef {object} CartCapConfig
 * @property {number} variantId - The PDP variant.
 * @property {number | null} inventory - Tracked, policy-deny inventory; null = inventory does not cap.
 * @property {boolean} soldOut - The variant is sold out (server state only).
 * @property {Record<'inventory' | 'soldOut', string>} messages - Limit sentences ({count} placeholder).
 *
 * @typedef {{ variant_id: number | string, quantity: number }} CartLine
 *
 * @typedef {object} Refs
 * @property {HTMLElement} reserveArea - The primary Reserve button's wrapper, observed.
 * @property {HTMLElement} stickyBar - The fixed bottom bar, toggled via the `_visible` class.
 * @property {HTMLScriptElement} [cartCapConfig] - The cart-limit JSON.
 * @property {HTMLElement[]} [limitMessages] - The limit sentence under each CTA.
 * @property {HTMLInputElement} [sellingPlanInput] - The hidden Downpay plan input (reserve with a plan).
 * @property {HTMLInputElement[]} [paymentOptions] - The "deposit" / "full" radio tiles (rendered with the plan input).
 *
 * @extends {Component<Refs>}
 */
class PdpStickyReserveComponent extends Component {
  requiredRefs = ['reserveArea', 'stickyBar'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {CartCapConfig | null} */
  #cartCap = null;

  /** Whether one more of this variant fits the cart (server state first). */
  #canAdd = true;

  /** Whether this form's own add-to-cart request is in flight. */
  #busy = false;

  /** @type {AbortController | null} */
  #cartRequest = null;

  /** The ranch's shipping lanes (state codes) from `data-lanes`. */
  #lanes = [];

  /** The no-service sentence template from `data-no-service-message`. */
  #noServiceTemplate = '';

  /** @type {CartLine[] | null} The cart lines of the last evaluation. */
  #lastItems = null;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(this.#toggleOnIntersect, { threshold: 0 });
    this.#observer.observe(this.refs.reserveArea);

    this.#cartCap = this.#readCartCapConfig();
    this.#canAdd = !this.classList.contains('_limit');
    this.#lanes = (this.dataset.lanes ?? '').split(' ').filter(Boolean);
    this.#noServiceTemplate = this.dataset.noServiceMessage ?? '';
    this.#syncPaymentChoice();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#lockAndResyncOnCartUpdate);
    document.addEventListener(DESTINATION_STATE_CHANGE, this.#reapplyOnDestinationChange);
    window.addEventListener('pageshow', this.#resyncOnBfcacheRestore);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#lockAndResyncOnCartUpdate);
    document.removeEventListener(DESTINATION_STATE_CHANGE, this.#reapplyOnDestinationChange);
    window.removeEventListener('pageshow', this.#resyncOnBfcacheRestore);
    this.#cartRequest?.abort();
    this.#setBusy(false);
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #toggleOnIntersect = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
    this.refs.stickyBar.classList.toggle('_visible', scrolledPast);
  };

  /**
   * Locks both Reserve buttons for the lifetime of an add-to-cart request
   * started by the product form that wraps this component, and — for every
   * cart change on the page (this add, drawer quantity updates, removals) —
   * re-evaluates the cart limit once the change settles.
   * @param {Event & {action?: string, promise?: Promise<any>}} event
   */
  #lockAndResyncOnCartUpdate = (event) => {
    // Only this form's own add locks the buttons; a cart-drawer update
    // (action "update"/"remove") or another form only triggers the resync.
    const ownAdd = event.target instanceof Node && event.target.contains(this) && event.action === 'add';
    if (ownAdd) this.#setBusy(true);

    if (!this.#cartCap && !ownAdd) return;

    const settled = event.promise ?? Promise.resolve(undefined);
    settled
      .then((result) => {
        const items = result?.detail?.items;
        if (Array.isArray(items)) {
          this.#cartRequest?.abort();
          this.#applyCart(items);
          return undefined;
        }
        return this.#refreshFromCart();
      })
      .catch(() => this.#refreshFromCart())
      .finally(() => {
        if (ownAdd) this.#setBusy(false);
      });
  };

  /**
   * The shopper picked another state: re-run the rule on the lines already
   * seen (the cart itself did not change), else read the cart once.
   */
  #reapplyOnDestinationChange = () => {
    if (this.#lastItems) {
      this.#applyCart(this.#lastItems);
    } else {
      this.#refreshFromCart();
    }
  };

  /**
   * @param {PageTransitionEvent} event
   */
  #resyncOnBfcacheRestore = (event) => {
    if (!event.persisted) return;
    this.#syncPaymentChoice();
    this.#refreshFromCart();
  };

  /**
   * A payment tile changed (declarative `on:change` on both radios).
   */
  applyPaymentChoice = () => {
    this.#syncPaymentChoice();
  };

  /**
   * Applies the checked payment tile: the full tile disables the hidden
   * `selling_plan` input and flags the buy box root with `_full-payment`;
   * the deposit tile does the opposite. Nothing to do when the product
   * renders no tiles (in stock, or reserve without a plan); the plan input
   * and the tiles are rendered together, so one without the other is a
   * broken markup contract.
   */
  #syncPaymentChoice() {
    const { sellingPlanInput, paymentOptions } = this.refs;
    const hasInput = sellingPlanInput instanceof HTMLInputElement;
    const hasOptions = Boolean(paymentOptions?.length);
    if (!hasInput && !hasOptions) return;
    if (!hasInput || !hasOptions) {
      throw new Error('pdp-sticky-reserve-component: sellingPlanInput and paymentOptions[] must be rendered together');
    }

    const buyBox = this.closest('.js-pdp-buy-box');
    if (!buyBox) throw new Error('pdp-sticky-reserve-component: missing .js-pdp-buy-box ancestor');

    const fullPayment = paymentOptions.some((option) => option.checked && option.value === 'full');
    sellingPlanInput.disabled = fullPayment;
    buyBox.classList.toggle('_full-payment', fullPayment);
  }

  /**
   * @returns {CartCapConfig | null}
   */
  #readCartCapConfig() {
    const { cartCapConfig } = this.refs;
    if (!(cartCapConfig instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(cartCapConfig.textContent ?? '');
    } catch (error) {
      throw new Error('pdp-sticky-reserve-component: cartCapConfig is not valid JSON', { cause: error });
    }
  }

  /**
   * Reads the live cart and re-applies the limit. On failure the last known
   * state stays (the server re-renders the truth on the next page load).
   */
  async #refreshFromCart() {
    if (!this.#cartCap) return;

    this.#cartRequest?.abort();
    const controller = new AbortController();
    this.#cartRequest = controller;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

      const cart = await response.json();
      this.#applyCart(Array.isArray(cart?.items) ? cart.items : []);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('[pdp-sticky-reserve] Cart refresh failed; keeping the last known limit state.', error);
    } finally {
      if (this.#cartRequest === controller) this.#cartRequest = null;
    }
  }

  /**
   * @param {CartLine[]} items
   */
  #applyCart(items) {
    if (!this.#cartCap) return;
    this.#lastItems = items;

    const { reason, message } = this.#evaluateCart(this.#cartCap, items);
    this.#canAdd = reason === '';

    this.classList.toggle('_limit', !this.#canAdd);
    this.classList.toggle('_no-service', reason === 'no_service');

    // The no-service label (shown by `_no-service`) follows the state name.
    const noServiceLabel = this.#noServiceTemplate.split(STATE_PLACEHOLDER).join(getStateInfo()?.name ?? '');
    for (const label of this.querySelectorAll('.js-pdp-no-service-label')) {
      if (label.textContent !== noServiceLabel) label.textContent = noServiceLabel;
    }
    if (this.#canAdd) {
      this.removeAttribute('aria-disabled');
    } else {
      this.setAttribute('aria-disabled', 'true');
    }

    for (const element of this.refs.limitMessages ?? []) {
      element.textContent = message;
      element.hidden = message === '';
    }

    this.#syncButtons();
  }

  /**
   * Whether the ranch ships to the shopper's current state. No state (the
   * store resolved nothing) never blocks — the server renders the same.
   * @returns {boolean}
   */
  #shipsToDestination() {
    const code = getDestinationState();
    return !code || this.#lanes.includes(code);
  }

  /**
   * Client mirror of snippets/ranch-cart-cap.liquid plus the buy box's
   * no-service rule: same reasons and priority — no_service > sold_out >
   * inventory — and the same sentences. One more fits when the ranch ships
   * here, the variant is not sold out and its cart quantity + 1 stays within
   * inventory (always, when inventory does not cap).
   * @param {CartCapConfig} cap
   * @param {CartLine[]} items
   * @returns {{ reason: string, message: string }}
   */
  #evaluateCart(cap, items) {
    const ownId = String(cap.variantId);

    let cartQuantity = 0;
    for (const item of items) {
      if (String(item.variant_id) === ownId) cartQuantity += Number(item.quantity) || 0;
    }

    let reason = '';
    if (!this.#shipsToDestination()) {
      reason = 'no_service';
    } else if (cap.soldOut) {
      reason = 'sold_out';
    } else if (cap.inventory !== null && cartQuantity + 1 > cap.inventory) {
      reason = 'inventory';
    }

    const { messages } = cap;
    /** @type {Record<string, string>} */
    const sentences = {
      // No service is the CTA label itself (see #applyCart), not a sentence.
      no_service: '',
      sold_out: messages.soldOut,
      inventory: messages.inventory.replace('{count}', String(cap.inventory)),
    };

    return { reason, message: sentences[reason] ?? '' };
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    this.classList.toggle('_loading', busy);
    if (busy) {
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }

    this.#syncButtons();
  }

  /**
   * Buttons are enabled only when no add is in flight AND the cart limit
   * allows one more — through the add-to-cart components' public API.
   */
  #syncButtons() {
    const enabled = !this.#busy && this.#canAdd;

    for (const addToCart of this.querySelectorAll('add-to-cart-component')) {
      const control = /** @type {{ disable?: () => void, enable?: () => void }} */ (addToCart);
      if (!enabled && typeof control.disable === 'function') control.disable();
      if (enabled && typeof control.enable === 'function') control.enable();
    }
  }
}

if (!customElements.get('pdp-sticky-reserve-component')) {
  customElements.define('pdp-sticky-reserve-component', PdpStickyReserveComponent);
}
